/**
 * Blocky Striker API helpers. Endless run flow: start today's session, then report each finished run. The
 * server owns the economy — coins depend only on server-clamped goals/pickups + the per-day cap, never on
 * a client-supplied amount. points/distance/cause are advisory (analytics/UI) only.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { BLOCKY_STRIKER_GAME_KEY } from '../constants/blockyStriker.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface BlockyStrikerStart {
  coinsPerGoal: number;
  maxPaidGoalsPerRun: number;
  maxPaidPickupsPerRun: number;
  dailyCoinCap: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestGoals: number;
}

export interface BlockyStrikerEndResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestGoals: number;
  newBest: boolean;
}

export async function startBlockyStriker(apiClient: ApiClient): Promise<BlockyStrikerStart> {
  const response = await apiClient<{ success: boolean; data: BlockyStrikerStart; message?: string }>(
    API_ENDPOINTS.games.runStart(BLOCKY_STRIKER_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function recordBlockyStrikerEnd(
  apiClient: ApiClient,
  args: { goals: number; pickups: number },
): Promise<BlockyStrikerEndResult> {
  const response = await apiClient<{ success: boolean; data: BlockyStrikerEndResult; message?: string }>(
    API_ENDPOINTS.games.runEnd(BLOCKY_STRIKER_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the run');
  return payload.data;
}
